import React, { useState, useEffect } from "react";
import { projectAPI } from "../../services/api";
import { X, Info, Users, AlertCircle } from "lucide-react";
import { toast } from "react-toastify";
import { useContractServices } from "../../context/ContractServicesContext";
import { ethers } from "ethers";

const EditProjectForm = ({ project, onClose }) => {
  const { services, isReady } = useContractServices();
  const [creators, setCreators] = useState([]);
  const [loadingCreators, setLoadingCreators] = useState(false);
  
  const [formData, setFormData] = useState({
    shortDescription: project.shortDescription || project.description || "",
    detailedDescription: project.detailedDescription || project.problemAddressed || "",
    primaryDomain: project.primaryDomain || project.industry || "",
    subdomains: project.subdomains || [],
    relevantUrls: project.relevantUrls || [],
    newUrl: "",
    targetedPlatforms: project.targetedPlatforms || [],
    newPlatform: "",
    intendedAudience: project.intendedAudience || [],
    newAudience: "",
    projectRoadmap: project.projectRoadmap || "",
    riskAssessment: project.riskAssessment || "",
    budgetAllocation: project.budgetAllocation || "",
    contactInformation: project.contactInformation || "",
    openForCollaboration: project.openForCollaboration || false,
    contributionGuidelines: project.contributionGuidelines || "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Fetch creator info from the blockchain when component mounts
  useEffect(() => {
    const fetchCreatorInfo = async () => {
      if (!isReady || !services?.innovationUnits || !project?.projectTokenId) {
        return;
      }

      try {
        setLoadingCreators(true);
        
        // Convert projectTokenId to a number if it's a string
        const projectId = typeof project.projectTokenId === 'string' 
          ? parseInt(project.projectTokenId)
          : project.projectTokenId;
        
        console.log(`Fetching creator info for project ID: ${projectId}`);
        
        const creatorsData = await services.innovationUnits.getCreatorInfo(projectId);
        console.log("Creators data from contract:", creatorsData);
        
        if (creatorsData && creatorsData.creators.length > 0) {
          // Transform data for display
          const formattedCreators = creatorsData.creators.map((address, index) => ({
            walletAddress: address,
            sharePercentage: (creatorsData.shares[index] / 100).toFixed(2),
            shareRaw: creatorsData.shares[index]
          }));
          
          setCreators(formattedCreators);
        }
      } catch (error) {
        console.error("Error fetching creator info:", error);
      } finally {
        setLoadingCreators(false);
      }
    };

    fetchCreatorInfo();
  }, [project, isReady, services]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleArrayChange = (field, action, value = "", index = -1) => {
    if (action === "add" && value.trim()) {
      setFormData(prev => ({
        ...prev,
        [field]: [...prev[field], value.trim()],
        [`new${field.charAt(0).toUpperCase() + field.slice(1, -1)}`]: "" // Reset the input field
      }));
    } else if (action === "remove" && index >= 0) {
      setFormData(prev => ({
        ...prev,
        [field]: prev[field].filter((_, i) => i !== index)
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      // Remove temporary form fields that shouldn't be sent to the API
      const { newUrl, newPlatform, newAudience, ...submissionData } = formData;
      
      // Making sure arrays are properly formatted
      submissionData.relevantUrls = submissionData.relevantUrls.filter(url => url);
      submissionData.targetedPlatforms = submissionData.targetedPlatforms.filter(platform => platform);
      submissionData.intendedAudience = submissionData.intendedAudience.filter(audience => audience);
      const response = await projectAPI.update(project.projectTokenId, submissionData);
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      toast.error(`Something went wrong: ${err}`);
      setError(err.message || "Failed to update project");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Edit Project</h2>
        <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
          <X className="w-5 h-5" />
        </button>
      </div>
      
      {/* Creator Information Box */}
      <div className="mb-6 bg-indigo-50 p-4 rounded-md border-l-4 border-indigo-500">
        <h3 className="text-md font-medium flex items-center text-indigo-700 mb-2">
          <Users className="w-5 h-5 mr-2" />
          Project Creators (Blockchain Data)
        </h3>
        
        {loadingCreators ? (
          <p className="text-sm text-indigo-600">Loading creator information from smart contract...</p>
        ) : creators.length > 0 ? (
          <div>
            <p className="text-sm text-indigo-600 mb-3">
              This information is retrieved directly from the blockchain and shows the registered creators and their ownership percentage.
            </p>
            <div className="space-y-2">
              {creators.map((creator, index) => (
                <div key={index} className="flex justify-between items-center bg-white p-3 rounded border border-indigo-100">
                  <div className="text-sm">
                    <div className="font-medium text-gray-700 truncate">{creator.walletAddress}</div>
                    <div className="text-xs text-gray-500">Project Creator</div>
                  </div>
                  <div className="text-sm bg-indigo-100 px-3 py-1 rounded-full text-indigo-800 font-medium">
                    {creator.sharePercentage}% Share
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-3">
              Note: Creator allocations are set during project creation and cannot be changed afterward.
            </p>
          </div>
        ) : (
          <div>
            <p className="text-sm text-gray-600 mb-2">No creator information available from blockchain. This might happen if the project was created before the latest contract updates.</p>
            <p className="text-xs text-gray-500">If you believe this is an error, please contact support with your project ID: {project.projectTokenId}</p>
          </div>
        )}
      </div>
      
      <div className="mb-6 bg-blue-50 p-4 rounded-md flex items-start">
        <Info className="w-5 h-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-blue-700">
          You can edit project details like descriptions, target audience, and additional information. 
          However, tokenomics and investment details cannot be modified after project creation.
        </p>
      </div>

      <div className="mb-6 bg-amber-50 p-4 rounded-md flex items-start">
        <AlertCircle className="w-5 h-5 text-amber-500 mr-2 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm text-amber-700 font-medium mb-1">
            Important Note for Project Creators
          </p>
          <p className="text-sm text-amber-700">
            Changes made here will update the project metadata on our platform, but won't affect the blockchain data. 
            Creator allocations, token distributions, and other on-chain parameters cannot be modified after project creation.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* Project Overview Section */}
          <div className="border rounded-lg p-4 bg-gray-50">
            <h3 className="text-lg font-medium mb-4">Project Overview</h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Short Description (280 characters max)
              </label>
              <textarea
                name="shortDescription"
                value={formData.shortDescription}
                onChange={handleChange}
                className="w-full border rounded-md p-2 focus:ring-indigo-500 focus:border-indigo-500"
                rows="2"
                maxLength="280"
                required
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Detailed Description
              </label>
              <textarea
                name="detailedDescription"
                value={formData.detailedDescription}
                onChange={handleChange}
                className="w-full border rounded-md p-2 focus:ring-indigo-500 focus:border-indigo-500"
                rows="5"
                required
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Primary Domain
              </label>
              <select
                name="primaryDomain"
                value={formData.primaryDomain}
                onChange={handleChange}
                className="w-full border rounded-md p-2 focus:ring-indigo-500 focus:border-indigo-500"
                required
              >
                <option value="">Select a domain</option>
                <option value="Finance">Finance</option>
                <option value="Healthcare">Healthcare</option>
                <option value="Education">Education</option>
                <option value="Technology">Technology</option>
                <option value="Environment">Environment</option>
                <option value="Social">Social</option>
                <option value="Entertainment">Entertainment</option>
                <option value="Energy">Energy</option>
                <option value="Manufacturing">Manufacturing</option>
                <option value="Transport">Transport</option>
                <option value="Other">Other</option>
              </select>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Subdomains
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {formData.subdomains.map((subdomain, index) => (
                  <div key={index} className="bg-indigo-100 rounded-full px-3 py-1 flex items-center">
                    <span className="text-sm text-indigo-800">{subdomain}</span>
                    <button
                      type="button"
                      onClick={() => handleArrayChange("subdomains", "remove", "", index)}
                      className="ml-1 text-indigo-700 hover:text-indigo-900"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex">
                <input
                  type="text"
                  value={formData.newSubdomain || ""}
                  onChange={(e) => setFormData({...formData, newSubdomain: e.target.value})}
                  className="flex-grow border rounded-l-md p-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Add a subdomain"
                />
                <button
                  type="button"
                  onClick={() => handleArrayChange("subdomains", "add", formData.newSubdomain)}
                  className="bg-indigo-600 text-white px-4 rounded-r-md hover:bg-indigo-700"
                >
                  Add
                </button>
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Relevant URLs (Add websites, repositories, etc.)
              </label>
              <div className="space-y-2 mb-2">
                {formData.relevantUrls.map((url, index) => (
                  <div key={index} className="flex items-center">
                    <input
                      type="text"
                      value={url}
                      disabled
                      className="flex-grow border rounded-md p-2 bg-gray-100"
                    />
                  </div>
                ))}
              </div>
              <div className="flex">
                <input
                  type="text"
                  value={formData.newUrl}
                  onChange={(e) => setFormData({...formData, newUrl: e.target.value})}
                  className="flex-grow border rounded-l-md p-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="https://example.com"
                />
                <button
                  type="button"
                  onClick={() => handleArrayChange("relevantUrls", "add", formData.newUrl)}
                  className="bg-indigo-600 text-white px-4 rounded-r-md hover:bg-indigo-700"
                >
                  Add
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-1">Note: You can add new URLs but cannot remove existing ones.</p>
            </div>
          </div>

          {/* Target Audience Section */}
          <div className="border rounded-lg p-4 bg-gray-50">
            <h3 className="text-lg font-medium mb-4">Target Information</h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Targeted Platforms
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {formData.targetedPlatforms.map((platform, index) => (
                  <div key={index} className="bg-green-100 rounded-full px-3 py-1 flex items-center">
                    <span className="text-sm text-green-800">{platform}</span>
                    <button
                      type="button"
                      onClick={() => handleArrayChange("targetedPlatforms", "remove", "", index)}
                      className="ml-1 text-green-700 hover:text-green-900"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex">
                <input
                  type="text"
                  value={formData.newPlatform}
                  onChange={(e) => setFormData({...formData, newPlatform: e.target.value})}
                  className="flex-grow border rounded-l-md p-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Add a platform (e.g., Web, Mobile, Desktop)"
                />
                <button
                  type="button"
                  onClick={() => handleArrayChange("targetedPlatforms", "add", formData.newPlatform)}
                  className="bg-green-600 text-white px-4 rounded-r-md hover:bg-green-700"
                >
                  Add
                </button>
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Intended Audience
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {formData.intendedAudience.map((audience, index) => (
                  <div key={index} className="bg-purple-100 rounded-full px-3 py-1 flex items-center">
                    <span className="text-sm text-purple-800">{audience}</span>
                    <button
                      type="button"
                      onClick={() => handleArrayChange("intendedAudience", "remove", "", index)}
                      className="ml-1 text-purple-700 hover:text-purple-900"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex">
                <input
                  type="text"
                  value={formData.newAudience}
                  onChange={(e) => setFormData({...formData, newAudience: e.target.value})}
                  className="flex-grow border rounded-l-md p-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Add target audience (e.g., Developers, Businesses)"
                />
                <button
                  type="button"
                  onClick={() => handleArrayChange("intendedAudience", "add", formData.newAudience)}
                  className="bg-purple-600 text-white px-4 rounded-r-md hover:bg-purple-700"
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Add buttons section */}
        <div className="mt-8 flex justify-end space-x-4">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className={`px-6 py-2 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200 ${
              loading 
                ? 'bg-indigo-400 cursor-not-allowed' 
                : 'bg-indigo-600 hover:bg-indigo-700'
            }`}
          >
            {loading ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </span>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditProjectForm; 